import api from './api';
import * as XLSX from 'xlsx-js-style';

// Types for yield variance data
export interface YieldVarianceOperator {
  id: string | null;
  name: string | null;
  employee_number: string | null;
}

export interface YieldVarianceStation {
  id: number | null;
  name: string | null;
}

export interface YieldVarianceRawMaterial {
  item_number: string | null;
  name: string | null;
  chinese_name: string | null;
}

export interface YieldVarianceApproval {
  supervisor_name: string;
  reason: string;
  approved_at: string;
}

export interface WasteBreakdownItem {
  waste_type: string;
  waste_weight: number;
}

export interface YieldVarianceRecord {
  sub_session_number: string;
  session_number: string;
  product_sku: string;
  product_name: string;
  product_chinese_name: string | null;
  completed_at: string;
  actual_yield: number;
  expected_yield: number | null;  // null when the (product, material) pair has no configured yield
  expected_yield_source: string | null;
  variance: number | null;
  actual_output_weight: number;
  target_output_weight: number;
  initial_material_weight: number;
  completion_type: 'normal' | 'override';
  operator: YieldVarianceOperator;
  station: YieldVarianceStation;
  raw_material: YieldVarianceRawMaterial;
  // WCO-2474: Labour Factor is standard-vs-actual (was rolling-average).
  // WCO-2609: the LF variance columns are SELF-RELATIVE — each material vs its OWN standard, so
  // expected is pinned to 1.00 and actual is normalized (material_std ÷ observed).
  labour_factor: number | null;          // backward-compat alias of actual_labour_factor (self-relative)
  actual_labour_factor: number | null;   // self-relative LF = material_std ÷ observed (NULL pre-stamp)
  expected_labour_factor: number | null; // 1.00 for every material (each is its own 1.00 reference)
  observed_throughput: number | null;    // persisted lb/worker-hour (NULL pre-stamp)
  // WCO-2627: the throughput's own inputs, so a manager can reconcile it at any crew size.
  productive_seconds: number | null;     // elapsed − pauses, clamped ≥ 0 (NULL pre-stamp/bad timestamps)
  worker_count: number;                  // distinct crew members (0 when none recorded)
  labour_factor_variance: number | null; // 1.00 − actual (POSITIVE = beat own standard/better)
  variance_pct: number | null;           // (actual − 1.00) × 100 (POSITIVE = above standard/slower/worse)
  needs_review: boolean;                 // ±20% anomaly flag
  lf_data_quality_status: string | null; // raw curation/anomaly status
  approval: YieldVarianceApproval | null;
  waste_breakdown: WasteBreakdownItem[];
}

export interface YieldVarianceSummary {
  total_variances: number;
  over_yield_count: number;
  under_yield_count: number;
  avg_variance: number;
}

export interface YieldVariancePagination {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface YieldVarianceResponse {
  variances: YieldVarianceRecord[];
  summary: YieldVarianceSummary;
  pagination: YieldVariancePagination;
}

export interface YieldVarianceFilters {
  startDate?: string;
  endDate?: string;
  productSku?: string;
  operatorId?: string;
  stationId?: string;
  varianceType?: 'over' | 'under' | 'all';
  limit?: number;
  offset?: number;
}

// Stable filter dropdown options — distinct values across ALL yield-variance-eligible
// rows, independent of the active filters (WCO-2612). Shapes match the report's
// dropdown state exactly, so the component can set them verbatim.
export interface YieldVarianceFilterOptions {
  products: Array<{ sku: string; name: string }>;
  operators: Array<{ id: string; name: string }>;
  stations: Array<{ id: string; name: string }>;
}

// Types for yield variance suggestions (recurring variances per product + raw material)
export interface YieldVarianceSuggestionEntry {
  actual_yield: number;
  expected_yield: number | null;
  variance: number | null;
  session_number: string;
  sub_session_number: string;
  completed_at: string;
  operator_name: string | null;
  packer_name: string | null;
}

export interface YieldVarianceSuggestion {
  product_sku: string;
  product_name: string;
  product_chinese_name: string | null;
  item_number: string;
  item_name: string;
  item_chinese_name: string | null;
  variance_count: number;
  current_expected_yield: number | null;  // null when the (product, material) pair has no configured yield
  yield_source: string | null;
  suggested_yield: number;
  last_occurrence: string;
  recent_variances: YieldVarianceSuggestionEntry[];
}

export interface YieldVarianceSuggestionsResponse {
  suggestions: YieldVarianceSuggestion[];
  total: number;
  threshold: number;
}

// Service functions
const yieldVarianceService = {
  /**
   * Fetch yield variance records with optional filters
   */
  getYieldVariances: async (filters: YieldVarianceFilters = {}): Promise<YieldVarianceResponse> => {
    const params = new URLSearchParams();

    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.productSku) params.append('productSku', filters.productSku);
    if (filters.operatorId) params.append('operatorId', filters.operatorId);
    if (filters.stationId) params.append('stationId', filters.stationId);
    if (filters.varianceType) params.append('varianceType', filters.varianceType);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    const queryString = params.toString();
    // Note: api base URL already includes '/api', so we don't include it here
    const url = `/production/sessions/yield-variances${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);
    const data: YieldVarianceResponse = response.data;
    // Normalize yield fields from whole % to decimal (preserve null — unset yield = data gap)
    for (const v of data.variances) {
      v.actual_yield = Number(v.actual_yield) / 100;
      v.expected_yield = v.expected_yield == null ? null : Number(v.expected_yield) / 100;
      v.variance = v.variance == null ? null : Number(v.variance) / 100;
    }
    if (data.summary) {
      data.summary.avg_variance = Number(data.summary.avg_variance) / 100;
    }
    return data;
  },

  /**
   * Fetch the stable filter dropdown options (distinct products / operators / stations across
   * all yield-variance-eligible rows). Unaffected by the active filters — call once on mount so
   * the dropdowns don't collapse to the current result set (WCO-2612).
   */
  getYieldVarianceFilterOptions: async (): Promise<YieldVarianceFilterOptions> => {
    const url = '/production/sessions/yield-variances/filter-options';
    const response = await api.get(url);
    const data = response.data || {};
    return {
      products: (data.products || []).map((p: any) => ({ sku: p.sku, name: p.name || p.sku })),
      // Preserve the report's original operator label fallback: name → employee_number → id
      operators: (data.operators || []).map((o: any) => ({
        id: String(o.id),
        name: o.name || o.employee_number || String(o.id)
      })),
      stations: (data.stations || []).map((s: any) => ({ id: String(s.id), name: s.name || '' }))
    };
  },

  /**
   * Export yield variances to Excel
   */
  /**
   * Fetch materials with recurring yield variances (2+ occurrences) and suggested yield factor updates.
   * Yields are returned as whole % from the backend.
   */
  getYieldVarianceSuggestions: async (): Promise<YieldVarianceSuggestionsResponse> => {
    const url = '/production/sessions/yield-variance-suggestions';
    const response = await api.get(url);
    const data: YieldVarianceSuggestionsResponse = response.data;
    // Normalize yields from whole % to decimal (preserve null — unset yield = data gap)
    for (const s of data.suggestions) {
      s.current_expected_yield = s.current_expected_yield == null ? null : Number(s.current_expected_yield) / 100;
      s.suggested_yield = Number(s.suggested_yield) / 100;
      for (const v of s.recent_variances) {
        v.actual_yield = Number(v.actual_yield) / 100;
        v.expected_yield = v.expected_yield == null ? null : Number(v.expected_yield) / 100;
        v.variance = v.variance == null ? null : Number(v.variance) / 100;
      }
    }
    return data;
  },

  /**
   * Lightweight count of materials with recurring yield variances
   */
  getYieldVarianceSuggestionCount: async (): Promise<number> => {
    const url = '/production/sessions/yield-variance-suggestions/count';
    const response = await api.get(url);
    return response.data.count;
  },

  exportToExcel: (
    variances: YieldVarianceRecord[],
    filename: string = 'yield-variances.xlsx',
    carryoverBySubSession: Record<string, Array<{ allocated_weight: number | string }>> = {}
  ): void => {
    const headers = [
      'Session',
      'Date',
      'Raw Material',
      'Product',
      'Expected Yield %',
      'Actual Yield %',
      'Variance %',
      'Operator',
      'Station',
      'Approved By',
      'Reason',
      'Input Weight (lb)',
      'Output Weight (lb)',
      'Carryover - FP Not Intact (lb)',
      'Target Weight (lb)',
      'Waste - By Product',
      'Waste - Disposable',
      'Waste - Water/Liquid',
      'Waste - Human Error',
      'Waste - Remainder Intact',
      'Waste - Remainder Not Intact',
      'Waste - Finish Product Not Intact'
    ];

    // Format item display: "item# - English Name (Chinese Name)"
    const formatItem = (itemNumber: string | null, englishName: string | null, chineseName: string | null): string => {
      const parts: string[] = [];
      if (itemNumber) parts.push(itemNumber);
      if (englishName) parts.push(englishName);
      const base = parts.join(' - ') || '-';
      if (chineseName) return `${base} (${chineseName})`;
      return base;
    };

    const rows = variances.map(v => {
      // Extract waste by type
      const getWasteWeight = (type: string) => {
        const item = v.waste_breakdown?.find(w => w.waste_type === type);
        return item ? Number(item.waste_weight.toFixed(2)) : 0;
      };

      const carryoverTotal = (carryoverBySubSession[v.sub_session_number] || [])
        .reduce((sum, c) => sum + (Number(c.allocated_weight) || 0), 0);

      return [
        v.sub_session_number || v.session_number,
        new Date(v.completed_at).toLocaleDateString(),
        formatItem(v.raw_material.item_number, v.raw_material.name, v.raw_material.chinese_name),
        formatItem(v.product_sku, v.product_name, v.product_chinese_name),
        v.expected_yield == null ? '-' : Number((v.expected_yield * 100).toFixed(2)),
        Number((v.actual_yield * 100).toFixed(2)),
        v.variance == null ? '-' : Number((v.variance * 100).toFixed(2)),
        v.operator.name || v.operator.employee_number || '-',
        v.station.name || '-',
        v.approval?.supervisor_name || '-',
        v.approval?.reason || '-',
        Number(v.initial_material_weight.toFixed(2)),
        Number(v.actual_output_weight.toFixed(2)),
        Number(carryoverTotal.toFixed(2)),
        Number(v.target_output_weight.toFixed(2)),
        getWasteWeight('by_product'),
        getWasteWeight('disposable'),
        getWasteWeight('water_liquid'),
        getWasteWeight('human_error'),
        getWasteWeight('remainder_intact'),
        getWasteWeight('remainder_not_intact'),
        getWasteWeight('finish_product_not_intact')
      ];
    });

    // Create worksheet with headers and data
    const wsData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Header style with gray background
    const headerStyle = {
      fill: { fgColor: { rgb: 'E5E7EB' } }, // gray-200
      font: { bold: true },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        bottom: { style: 'thin', color: { rgb: '9CA3AF' } }
      }
    };

    // Apply header style to first row
    headers.forEach((_, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      if (ws[cellRef]) {
        ws[cellRef].s = headerStyle;
      }
    });

    // Calculate column widths based on content (auto-fit)
    const columnWidths = headers.map((header, colIndex) => {
      let maxLength = header.length;
      rows.forEach(row => {
        const cellLength = String(row[colIndex]).length;
        if (cellLength > maxLength) maxLength = cellLength;
      });
      // Add some padding, min 10, max 50 characters
      return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
    });

    ws['!cols'] = columnWidths;

    // Create workbook and add worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Yield Variances');

    // Download file
    XLSX.writeFile(wb, filename);
  }
};

export default yieldVarianceService;
